import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useConfirmation } from '@/contexts/confirmation'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useInfiniteQueryWithHandler } from '@/hooks/useInfiniteQueryWithHandler'
import DeleteOutlineIcons from '@/icons/DeleteOutlineIcons'
import FileIcons from '@/icons/FileIcons'
import {
    deleteCustomerJourneyNote,
    getCustomerJourneyNotes,
} from '@/modules/Customer/services'
import { CustomerJourneyNote } from '@/modules/Customer/types'
import { parseImportantJourneyNote } from '@/modules/Customer/utils/notes'
import usePermissionsStore from '@/stores/usePermissionsStore'
import { formatDateWithFormatter } from '@/utils/function'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Loading } from 'vnetwork-ui'

interface JourneyStepNotesProps {
    customerId: number
    journeyId: number
    step: string
    isSystemNote: (content: string) => boolean
}

const JourneyStepNotes: React.FC<JourneyStepNotesProps> = ({
    customerId,
    journeyId,
    step,
    isSystemNote,
}) => {
    const { t } = useTranslation()
    const { setConfirmation } = useConfirmation()
    const { canDeleteCustomerJourneyNote } = usePermissionsStore()

    const { mutateAsync: deleteNoteAsync, isPending: isDeletingNote } =
        useMutationWithHandler(
            {
                mutationFn: deleteCustomerJourneyNote,
            },
            {
                reactQueryKeys: [
                    QUERY_KEYS.CUSTOMERS,
                    QUERY_KEYS.CUSTOMERS_JOURNEYS,
                    QUERY_KEYS.CUSTOMER_JOURNEY,
                    QUERY_KEYS.CUSTOMER_JOURNEY_LIST,
                ],
            }
        )

    const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
        useInfiniteQueryWithHandler<any, Error, any, any, number>(
            {
                queryKey: [
                    QUERY_KEYS.CUSTOMERS_JOURNEYS,
                    'notes',
                    journeyId,
                    step,
                ],
                queryFn: ({ pageParam = 1 }) =>
                    getCustomerJourneyNotes({
                        customer_id: customerId,
                        customer_journey_id: journeyId,
                        params: {
                            step,
                            page: pageParam,
                            page_size: 5,
                        },
                    }),
                initialPageParam: 1,
                getNextPageParam: (lastPage: any) => {
                    if (!lastPage?.pagination) return undefined
                    const { page, total_pages } = lastPage.pagination
                    return page < total_pages ? page + 1 : undefined
                },
            },
            {
                loadingComponent: (
                    <div className="py-2 flex justify-center">
                        <Loading.CircularProgress width={16} height={16} />
                    </div>
                ),
            }
        )

    const notes = useMemo(() => {
        const allNotes =
            data?.pages?.reduce((acc: CustomerJourneyNote[], page: any) => {
                return [...acc, ...(page?.results || [])]
            }, [] as CustomerJourneyNote[]) || []

        return allNotes.filter((note: CustomerJourneyNote) => {
            const { text } = parseImportantJourneyNote(
                note.customer_journey_note || ''
            )
            return !isSystemNote(text)
        })
    }, [data, isSystemNote])

    const canDeleteStepNote = useCallback(
        (note: CustomerJourneyNote) => {
            const hasLinkedActivity =
                Boolean(note.reference_id) || Boolean(note.reference_type)

            return canDeleteCustomerJourneyNote && !hasLinkedActivity
        },
        [canDeleteCustomerJourneyNote]
    )

    const handleDeleteNote = useCallback(
        (note: CustomerJourneyNote) => {
            setConfirmation({
                title: t('delete_confirmation'),
                description: t('delete_confirmation_description'),
                open: true,
                onConfirm: async () => {
                    await deleteNoteAsync({
                        customer_id: customerId,
                        customer_journey_id: journeyId,
                        customer_journey_note_id: note.customer_journey_note_id,
                    })
                },
            })
        },
        [customerId, deleteNoteAsync, journeyId, setConfirmation, t]
    )

    if (isLoading) {
        return (
            <div className="py-2 flex justify-center">
                <Loading.CircularProgress width={16} height={16} />
            </div>
        )
    }

    if (!notes || notes?.length === 0) {
        return null
    }

    return (
        <div className="space-y-1.5">
            {notes?.map((note: CustomerJourneyNote) => {
                const { text, isImportant } = parseImportantJourneyNote(
                    note.customer_journey_note || ''
                )

                return (
                    <div
                        key={note.customer_journey_note_id}
                        className={`p-2 rounded border transition-colors ${
                            isImportant
                                ? 'bg-red-50 border-red-200 hover:border-red-300'
                                : 'bg-white border-gray-100 hover:border-gray-200'
                        }`}
                    >
                        <div className="flex items-start gap-1.5">
                            <div
                                className={`flex-1 text-xs whitespace-pre-wrap break-words max-h-[60px] overflow-y-auto ${
                                    isImportant
                                        ? 'text-red-700'
                                        : 'text-gray-700'
                                }`}
                            >
                                {text}
                            </div>
                            {canDeleteStepNote(note) && (
                                <button
                                    type="button"
                                    title={t('delete')}
                                    className="shrink-0 p-0.5 text-gray-400 hover:text-red-500 disabled:opacity-50"
                                    disabled={isDeletingNote}
                                    onClick={() => handleDeleteNote(note)}
                                >
                                    <DeleteOutlineIcons className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>

                        {note.attachment && (
                            <a
                                href={note.attachment?.attachment_file_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-1.5 flex items-center gap-1.5 p-1.5 bg-gray-50 rounded text-[10px] text-gray-600 hover:bg-gray-100 transition-colors"
                            >
                                <FileIcons className="w-3 h-3 text-gray-400" />
                                <span className="truncate flex-1">
                                    {note.attachment?.attachment_file_name}
                                </span>
                            </a>
                        )}

                        <div className="mt-1.5 flex items-center gap-2 text-[10px] text-gray-400">
                            {note.employee?.employee_name && (
                                <span>{note.employee.employee_name}</span>
                            )}
                            {note.customer_journey_notes_time && (
                                <>
                                    <span>-</span>
                                    <span>
                                        {formatDateWithFormatter(
                                            note.customer_journey_notes_time,
                                            'DD/MM/YYYY HH:mm'
                                        )}
                                    </span>
                                </>
                            )}
                        </div>
                    </div>
                )
            })}

            {hasNextPage && (
                <button
                    className="w-full py-1 text-[10px] text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded transition-colors"
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                >
                    {isFetchingNextPage ? (
                        <Loading.CircularProgress width={12} height={12} />
                    ) : (
                        t('load-more')
                    )}
                </button>
            )}
        </div>
    )
}

export default JourneyStepNotes
